export interface AnimationState {
  topLeftRectAnim: number;
  topRightRectAnim: { x: number; y: number };
  bottomRectAnim: { x: number; y: number };
  fadeAnim: number;
  carRotation: number;
}

export interface AnimationStyles {
  topLeftRectStyle: React.CSSProperties;
  topRightRectStyle: React.CSSProperties;
  bottomRectStyle: React.CSSProperties;
}

class PhoneAnimationService {
  private animationPhase: number = 0;
  private onAnimationComplete: (() => void) | undefined = undefined;
  private animationState: AnimationState = {
    topLeftRectAnim: 0,
    topRightRectAnim: { x: 0, y: 0 },
    bottomRectAnim: { x: 0, y: 0 },
    fadeAnim: 0,
    carRotation: 0,
  };

  // Reset all animation values to initial state
  resetAnimationValues(): AnimationState {
    this.animationState = {
      topLeftRectAnim: 0,
      topRightRectAnim: { x: 0, y: 0 },
      bottomRectAnim: { x: 0, y: 0 },
      fadeAnim: 0,
      carRotation: 0,
    };
    this.animationPhase = 0;
    return this.animationState;
  }

  // Get interpolated rotation value for car animation
  getRotateInterpolated(rotation: number): string {
    if (rotation <= 1) {
      return `${rotation * -15}deg`;
    } else if (rotation <= 2) {
      return `${(2 - rotation) * -15}deg`;
    }
    return "0deg";
  }

  // Start the complete animation sequence
  async startAnimation(
    onPhaseChange?: (phase: number) => void,
    onComplete?: () => void
  ): Promise<void> {
    this.onAnimationComplete = onComplete || undefined;
    this.resetAnimationValues();
    
    // Initial fade in only once
    await this.animateFadeIn();
    
    // Start infinite car movement loop
    const runCarAnimation = async () => {
      await this.startCarMovementSequence(onPhaseChange);
      // When car movement completes, restart after a short delay
      setTimeout(() => runCarAnimation(), 1000);
    };
    
    // Start the infinite car movement loop
    setTimeout(() => runCarAnimation(), 300);
  }

  // Animate fade in
  private async animateFadeIn(): Promise<void> {
    return new Promise((resolve) => {
      const duration = 800;
      const startTime = performance.now();
      
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Smooth easing for fade
        const easedProgress = this.easeOutCubic(progress);
        
        this.animationState.fadeAnim = easedProgress;
        this.animationState.topLeftRectAnim = easedProgress;
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };
      
      requestAnimationFrame(animate);
    });
  }

  // Start the car movement sequence
  private async startCarMovementSequence(
    onPhaseChange?: (phase: number) => void
  ): Promise<void> {
    this.animationPhase = 1;
    onPhaseChange?.(this.animationPhase);
    
    // Animation positions
    const initialPosition = { x: 0, y: 0 };
    const moveOutPosition = { x: 35, y: 35 };
    const finalPosition = { x: 0, y: -45 };
    
    // 1. Move bottom car out of the way with rotation
    await this.animateBottomCarMove(moveOutPosition, 1, 1000);
    
    // 2. Top car exits
    await this.animateTopCarExit();
    
    // 3. Bottom car returns to original position then moves up
    await this.animateBottomCarReturn(initialPosition, 0, 600);
    await this.animateBottomCarMove(finalPosition, 0, 500);
    await this.animateTopCarReturn({ x: 0, y: 45 }, 1200);
    
    // Reset rectangles to initial positions before next cycle
    this.animationState.topRightRectAnim = { x: 0, y: 0 };
    this.animationState.bottomRectAnim = { x: 0, y: 0 };
    this.animationState.carRotation = 0;
    
    // Animation completes and will restart automatically
  }

  // Animate bottom car movement
  private async animateBottomCarMove(
    targetPosition: { x: number; y: number },
    targetRotation: number,
    duration: number
  ): Promise<void> {
    return new Promise((resolve) => {
      const startTime = performance.now();
      const startPosition = { ...this.animationState.bottomRectAnim };
      const startRotation = this.animationState.carRotation;
      
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Smooth easing for car movement
        const easedProgress = this.easeInOutCubic(progress);
        
        this.animationState.bottomRectAnim.x = startPosition.x + (targetPosition.x - startPosition.x) * easedProgress;
        this.animationState.bottomRectAnim.y = startPosition.y + (targetPosition.y - startPosition.y) * easedProgress;
        this.animationState.carRotation = startRotation + (targetRotation - startRotation) * easedProgress;
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };
      
      requestAnimationFrame(animate);
    });
  }

  // Animate top car exit
  private async animateTopCarExit(): Promise<void> {
    return new Promise((resolve) => {
      const duration = 1200;
      const startTime = performance.now();
      const startY = this.animationState.topRightRectAnim.y;
      const targetY = 300; // Exit downward (adjusted for smaller phone mockup)
      
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Smooth easing for exit
        const easedProgress = this.easeInOutCubic(progress);
        
        this.animationState.topRightRectAnim.y = startY + (targetY - startY) * easedProgress;
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };
      
      requestAnimationFrame(animate);
    });
  }

  // Animate bottom car return
  private async animateBottomCarReturn(
    targetPosition: { x: number; y: number },
    targetRotation: number,
    duration: number
  ): Promise<void> {
    return new Promise((resolve) => {
      const startTime = performance.now();
      const startPosition = { ...this.animationState.bottomRectAnim };
      const startRotation = this.animationState.carRotation;
      
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        const easedProgress = this.easeInOutCubic(progress);
        
        this.animationState.bottomRectAnim.x = startPosition.x + (targetPosition.x - startPosition.x) * easedProgress;
        this.animationState.bottomRectAnim.y = startPosition.y + (targetPosition.y - startPosition.y) * easedProgress;
        this.animationState.carRotation = startRotation + (targetRotation - startRotation) * easedProgress;
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };
      
      requestAnimationFrame(animate);
    });
  }

  // Animate top car return
  private async animateTopCarReturn(
    targetPosition: { x: number; y: number },
    duration: number
  ): Promise<void> {
    return new Promise((resolve) => {
      const startTime = performance.now();
      const startPosition = { ...this.animationState.topRightRectAnim };
      
      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        const easedProgress = this.easeInOutCubic(progress);
        
        this.animationState.topRightRectAnim.x = startPosition.x + (targetPosition.x - startPosition.x) * easedProgress;
        this.animationState.topRightRectAnim.y = startPosition.y + (targetPosition.y - startPosition.y) * easedProgress;
        
        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };
      
      requestAnimationFrame(animate);
    });
  }

  // Smooth easing functions
  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  private easeInOutCubic(t: number): number {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  // Get animation styles for the rectangles
  getAnimationStyles(): AnimationStyles {
    return {
      topLeftRectStyle: {
        opacity: this.animationState.topLeftRectAnim,
        transition: 'opacity 0.8s ease-out',
      },
      topRightRectStyle: {
        opacity: this.animationState.fadeAnim,
        transform: `translate3d(${this.animationState.topRightRectAnim.x}px, ${this.animationState.topRightRectAnim.y}px, 0px)`,
        transition: 'none',
      },
      bottomRectStyle: {
        opacity: this.animationState.fadeAnim,
        transform: `translate3d(${this.animationState.bottomRectAnim.x}px, ${this.animationState.bottomRectAnim.y}px, 0px) rotate(${this.getRotateInterpolated(this.animationState.carRotation)})`,
        transition: 'none',
      },
    };
  }

  // Get current animation state
  getAnimationState(): AnimationState {
    return { ...this.animationState };
  }

  // Get animation phase
  getAnimationPhase(): number {
    return this.animationPhase;
  }
}

export default PhoneAnimationService;
